import type { ContextMenuItem } from './types';
import AritoIcon from '../icon/icon';

type MenuSortDirection = 'asc' | 'desc' | null;

interface ColumnMenuHandlers {
  onSort?: (direction: MenuSortDirection) => void;
  onHide?: () => void;
  onCustomize?: () => void;
  onClearFilter?: () => void;
  onSearch?: (value: string) => void;
  currentSortDirection?: MenuSortDirection;
}

interface ColumnMenuContext {
  columnName: string;
}

type ColumnMenuSection = ContextMenuItem[];

type ColumnMenuItemConfig = Omit<ContextMenuItem, 'type'> & {
  type?: ContextMenuItem['type'];
};

const createMenuItem = ({ type = 'default', ...item }: ColumnMenuItemConfig): ContextMenuItem => ({
  ...item,
  type
});

const createSectionDivider = (id: string): ContextMenuItem =>
  createMenuItem({
    id,
    label: '',
    type: 'divider'
  });

const buildSortSection = ({ onSort, currentSortDirection }: ColumnMenuHandlers): ColumnMenuSection => {
  if (!onSort) {
    return [];
  }

  return [
    createMenuItem({
      id: 'sort-asc',
      label: 'Sắp xếp tăng dần',
      icon: <AritoIcon icon={13} />,
      type: 'radio',
      group: 'sort',
      selected: currentSortDirection === 'asc',
      onClick: () => onSort('asc')
    }),
    createMenuItem({
      id: 'sort-desc',
      label: 'Sắp xếp giảm dần',
      icon: <AritoIcon icon={24} />,
      type: 'radio',
      group: 'sort',
      selected: currentSortDirection === 'desc',
      onClick: () => onSort('desc')
    }),
    createMenuItem({
      id: 'sort-none',
      label: 'Không sắp xếp',
      icon: <AritoIcon icon={25} />,
      type: 'radio',
      group: 'sort',
      selected: currentSortDirection == null,
      onClick: () => onSort(null)
    })
  ];
};

const buildColumnSection = ({ onHide, onCustomize }: ColumnMenuHandlers): ColumnMenuSection => {
  const items: ColumnMenuSection = [];

  if (onHide) {
    items.push(
      createMenuItem({
        id: 'hide-column',
        label: 'Ẩn cột',
        icon: <AritoIcon icon={886} />,
        onClick: onHide
      })
    );
  }

  if (onCustomize) {
    items.push(
      createMenuItem({
        id: 'customize-column',
        label: 'Tùy chỉnh các cột',
        icon: <AritoIcon icon={887} />,
        onClick: onCustomize
      })
    );
  }

  return items;
};

const buildFilterSection = ({ columnName }: ColumnMenuContext, { onClearFilter, onSearch }: ColumnMenuHandlers) => {
  const items: ColumnMenuSection = [];

  if (onSearch) {
    items.push(
      createMenuItem({
        id: 'search',
        label: 'Tìm kiếm',
        type: 'search',
        shortcut: 'Ctrl+L'
      })
    );
  }

  if (onClearFilter) {
    items.push(
      createMenuItem({
        id: 'clear-filter',
        label: `Bỏ lọc cột [${columnName}]`,
        icon: <AritoIcon icon={861} />,
        onClick: onClearFilter
      })
    );
  }

  return items;
};

const buildMenuFromSections = (sections: ColumnMenuSection[]): ContextMenuItem[] => {
  const visibleSections = sections.filter(section => section.length > 0);

  return visibleSections.flatMap((section, index) => {
    if (index === 0) return section;
    return [createSectionDivider(`divider-${index}`), ...section];
  });
};

const normalizeColumnMenuArgs = (
  columnName: string,
  onSort?: (direction: MenuSortDirection) => void,
  onHide?: () => void,
  onCustomize?: () => void,
  onClearFilter?: () => void,
  onSearch?: (value: string) => void,
  currentSortDirection?: MenuSortDirection
): { context: ColumnMenuContext; handlers: ColumnMenuHandlers } => ({
  context: { columnName },
  handlers: {
    onSort,
    onHide,
    onCustomize,
    onClearFilter,
    onSearch,
    currentSortDirection
  }
});

export function createColumnMenuItems(
  columnName: string,
  onSort?: (direction: MenuSortDirection) => void,
  onHide?: () => void,
  onCustomize?: () => void,
  onClearFilter?: () => void,
  onSearch?: (value: string) => void,
  currentSortDirection?: MenuSortDirection
): ContextMenuItem[] {
  const { context, handlers } = normalizeColumnMenuArgs(
    columnName,
    onSort,
    onHide,
    onCustomize,
    onClearFilter,
    onSearch,
    currentSortDirection
  );

  return buildMenuFromSections([
    buildSortSection(handlers),
    buildColumnSection(handlers),
    buildFilterSection(context, handlers)
  ]);
}
